import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { usePortal } from '@/contexts/PortalContext';
import { LayoutDashboard, Construction, Camera, FileText, MessageSquare, CreditCard, Bell, LogOut, Menu } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useState } from 'react';
import PortalChatWidget from './PortalChatWidget';

const NAV = [
  { to: '/portal/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/portal/progress', icon: Construction, label: 'Progress' },
  { to: '/portal/photos', icon: Camera, label: 'Photos & Updates' },
  { to: '/portal/documents', icon: FileText, label: 'Documents' },
  { to: '/portal/billing', icon: CreditCard, label: 'Billing' },
  { to: '/portal/support', icon: MessageSquare, label: 'Support' },
];

export default function PortalLayout() {
  const { user, logout } = usePortal();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const handleLogout = () => { logout(); navigate('/portal/login'); };

  return (
    <div className="min-h-screen bg-[#e4f0f6]">
      {/* Top Nav */}
      <header className="bg-white border-b border-[#c8dce8]/50 sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="md:hidden" onClick={() => setMobileOpen(!mobileOpen)}>
              <Menu className="w-5 h-5 text-[#4a6a7e]" />
            </Button>
            <img src="/hdh-logo.png" alt="HDH" className="w-9 h-9 object-contain" />
            <div className="hidden sm:block">
              <p className="text-sm font-extrabold text-[#1e3a50] leading-tight">HDH Pools & Spas</p>
              <p className="text-[9px] font-bold text-[#4a9ac5] uppercase">Homeowner Portal</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs font-bold text-[#4a6a7e] hidden sm:block">{user?.name}</span>
            <Button variant="ghost" size="sm" onClick={handleLogout} className="text-[#8faab8] hover:text-red-500 text-xs">
              <LogOut className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
        {/* Nav tabs — desktop */}
        <div className="max-w-6xl mx-auto px-4 hidden md:flex gap-1 -mb-px">
          {NAV.map(({ to, icon: Icon, label }) => (
            <NavLink key={to} to={to}
              className={({ isActive }) => `flex items-center gap-1.5 px-4 py-2.5 text-xs font-bold border-b-2 transition-all ${
                isActive ? 'border-[#4a9ac5] text-[#2a6496]' : 'border-transparent text-[#6b8a9e] hover:text-[#2a4a60]'
              }`}>
              <Icon className="w-3.5 h-3.5" />{label}
            </NavLink>
          ))}
        </div>
      </header>
      {/* Mobile nav */}
      {mobileOpen && (
        <div className="md:hidden bg-white border-b border-[#c8dce8]/50 px-4 py-2 space-y-1">
          {NAV.map(({ to, icon: Icon, label }) => (
            <NavLink key={to} to={to} onClick={() => setMobileOpen(false)}
              className={({ isActive }) => `flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-bold ${
                isActive ? 'bg-[#4a9ac5]/10 text-[#2a6496]' : 'text-[#6b8a9e]'}`}>
              <Icon className="w-4 h-4" />{label}
            </NavLink>
          ))}
        </div>
      )}
      <main className="max-w-6xl mx-auto px-4 py-6">
        <Outlet />
      </main>
      <PortalChatWidget />
    </div>
  );
}
